import { useEffect, useMemo } from 'react'

import { Spinner } from '@wartek-id/spinner'

import { CardOrganizationProgress } from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardOrganizationProgress'
import CardFilledBySystem from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardFilledBySystem'
import CardReferensi from 'app/KinerjaPage/components/CardReferensi'
import CardEvaluasiKinerja from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardEvaluasiKinerja'
import CardKinerjaSteps from 'app/KinerjaShared/components/CardKinerjaSteps'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import CardKinerjaStatus from '../components/CardKinerjaStatus'
import CardPengaliranKinerjaStatus from '../components/CardPengaliranKinerjaStatus'
import CardPelaksanaanCompleted from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardPelaksanaanCompleted'
import { BannerGantiPeriode } from 'app/KinerjaShared/components/BannerGantiPeriode'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import { isAfterStep, isDateStarted } from 'app/KinerjaShared/utils/pelaksanaan'
import CardPelaksanaanNew from 'app/KinerjaPage/components/CardPelaksanaanNew'
import CardEvaluasiKinerjaTahunan from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardEvaluasiKinerjaTahunan'
import { InvalidUserNoAccess } from 'api/kinerja/kepala-sekolah/config'
import BannerPengaliranEkin from 'app/KinerjaShared/components/BannerPengaliranEkin'
import useToggleSdkContext from 'utils/useToogleSdk'
import usePelaksanaan from 'app/KinerjaPage/pages/PelaksanaanV2/hooks/usePelaksanaan'

interface BerandaGuruProps {
  isPengaliranKinerjaStatusEnabled?: boolean
}

const BerandaGuru = ({
  isPengaliranKinerjaStatusEnabled = false,
}: BerandaGuruProps) => {
  const {
    period: periodData,
    periods,
    periodNo,
    periodYear,
  } = usePeriodContext()

  const toggleValue = useToggleSdkContext()

  const nextPeriod = useMemo(() => {
    const indexPeriod = periods?.findIndex((item) => item.id === periodData?.id)
    return indexPeriod !== undefined && indexPeriod > -1
      ? periods[indexPeriod + 1]
      : null
  }, [periods, periodData])

  const formattedNextPeriod = useMemo(() => {
    if (nextPeriod) {
      return formatPeriod(nextPeriod.startDate, nextPeriod.endDate)
    }
    return ''
  }, [nextPeriod])

  const { isLoading, isGuru, isSuccess } = useRoleContext()

  const {
    isLoadingPelaksanaan,
    dataPelaksanaan,
    dataPerencanaan,
    isPerencanaanFetched,
    refetchSKP,
    isErrorPelaksanaan,
  } = usePelaksanaan(isGuru)

  useEffect(() => {
    if (isSuccess && !isGuru) {
      throw new InvalidUserNoAccess('', null)
    }
  }, [isGuru, isSuccess])

  const isResolved = Array.isArray(periods) && periods.length > 0
  const isPelaksanaanStarted =
    isResolved && isDateStarted(periodData.periodeKinerja)
  const isEvaluated = dataPelaksanaan?.data?.evaluationDocumentState === 'READY'
  const isAnnuallyEvaluated =
    dataPelaksanaan?.data?.annualEvaluationDocumentState === 'READY'
  const isPelaksanaanAfterFormD = isAfterStep(
    dataPelaksanaan?.data?.state,
    'DOCUMENT_REFLECTION_FOLLOW_UP'
  )

  const isGuruCompleted =
    isPelaksanaanAfterFormD &&
    dataPelaksanaan?.data?.realisasiState?.additionalTask === 'COMPLETED' &&
    dataPelaksanaan?.data?.realisasiState?.competencyImprovement === 'COMPLETED'

  const isNotReady = isLoading || isLoadingPelaksanaan

  return (
    <div className="pl-64">
      <div className="p-6 flex flex-col gap-6 w-full">
        {isNotReady ? (
          <div className="w-full flex justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <KinerjaSidebar sticky activeMenu="guru-pegawai" />
            {nextPeriod?.isApproved && (
              <BannerGantiPeriode
                title={`Periode ${formattedNextPeriod} sudah dimulai oleh atasan`}
                subtitle="Ganti periode untuk mulai perencanaan dan pelaksanaan. Tenang, periode sebelumnya tetap dapat diakses."
              />
            )}

            {toggleValue?.['kinerja-pengaliran-ekin-enabled'] && (
              <BannerPengaliranEkin jabatan="guru" />
            )}

            {dataPelaksanaan?.data?.filledBySystem && (
              <CardFilledBySystem
                isGuru={true}
                storageId={dataPelaksanaan?.data?.contentId || 'guru'}
                shouldInitiallyShowPopUp={true}
              />
            )}

            {isPengaliranKinerjaStatusEnabled && (
              <CardPengaliranKinerjaStatus
                statusUnor={dataPerencanaan?.data?.statusUnor}
                statusPengaliran={dataPerencanaan?.data?.statusPengaliran}
              />
            )}

            {isPelaksanaanAfterFormD &&
              !!dataPelaksanaan?.data?.organizationProgress &&
              isPelaksanaanStarted &&
              !isAnnuallyEvaluated && (
                <CardOrganizationProgress
                  activeStep={dataPelaksanaan.data.organizationProgress}
                />
              )}

            {isGuruCompleted && !isEvaluated && isPelaksanaanStarted && (
              <CardPelaksanaanCompleted period={periodNo} year={periodYear} />
            )}

            {isPelaksanaanStarted && isAnnuallyEvaluated && (
              <CardEvaluasiKinerjaTahunan year={periodYear} />
            )}

            {isEvaluated && isPelaksanaanStarted && <CardEvaluasiKinerja />}

            {/* if perencanaan fetched but perencanaan not ready or the Pelaksanaan is not started yet */}
            {isPerencanaanFetched &&
              (dataPerencanaan?.data?.status !== 'READY' ||
                !isPelaksanaanStarted) && (
                <CardKinerjaStatus
                  skpId={dataPerencanaan?.data?.id}
                  status={dataPerencanaan.data.status}
                  onSuccessUpdateSKP={refetchSKP}
                />
              )}

            {/* if perencanaan fetched and pelaksanaan already started */}
            {isPerencanaanFetched &&
              isPelaksanaanStarted &&
              dataPerencanaan?.data?.status === 'READY' && (
                <CardPelaksanaanNew
                  dataPelaksanaan={dataPelaksanaan?.data}
                  isError={isErrorPelaksanaan}
                />
              )}
          </>
        )}

        <CardKinerjaSteps />

        <CardReferensi />
      </div>
    </div>
  )
}

export default BerandaGuru
